// src/components/AgreementMatrices.js
//
// RULING 310 (Bob 2026-10-13, "yes"), item 4: in the agreements library, beside the agreements, the violation-matrix
// entries the General Chairperson has CONFIRMED (or corrected) and RELEASED - to his Local Chairpersons or to all the
// members of his committee - each marked "confirmed by the General Chairperson on [date]". Nothing unconfirmed, nothing
// struck, nothing unreleased is ever here: the server builds matrixPublished from his rulings only, and the rules let only
// the audience he chose read it. A query asks by committee AND audience, the way the rules prove it.
import React, { useEffect, useState } from 'react';
import { db } from '../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import RulingRef from './RulingRef';
import { SlidesButton } from './SlideDeck';   // ruling 311
import { matrixDeck } from '../data/slideDecks';

const C = { line: '#2E4A6B', dim: '#8AA4BF', accent: '#E8A020', bad: '#E06C6C', text: '#DCE7F2', quote: '#F4E7C8' };

function asks(user) {
  const c = user && user.committee;
  const col = collection(db, 'matrixPublished');
  if (!user) return [];
  if (user.isAdmin && !c) return [col];
  if (!c) return [];
  if (user.isAdmin || user.role === 'general_chair') return [query(col, where('committee', '==', c))];
  if (user.role === 'local_chair') return ['lc', 'members'].map(a => query(col, where('committee', '==', c), where('audience', '==', a)));
  return [query(col, where('committee', '==', c), where('audience', '==', 'members'))];
}

export default function AgreementMatrices({ user }) {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState('');
  const [openRule, setOpenRule] = useState('');
  // Forensic audit 2026-10-16: a failed read used to be .catch(() => []) - the released matrices silently missing, as
  // if the General Chairperson had released none. A failed read is now named, and what did read is still shown.
  const [readErr, setReadErr] = useState('');

  useEffect(() => {
    let live = true;
    setReadErr('');
    const failures = [];
    Promise.all(asks(user).map(q => getDocs(q).then(s => s.docs.map(d => d.data()))
      .catch((e) => { failures.push((e && (e.code || e.message)) || 'refused'); return []; })))
      .then(parts => { if (!live) return;
        if (failures.length) setReadErr('The released violation matrices could not be read (' + failures.join(', ') + '). What is shown may be incomplete - this is NOT "none released". Reload the page.');
        setList([].concat(...parts).filter(m => m.audience !== 'gc' && (m.entries || []).length)
        .sort((a, b) => String(a.title).localeCompare(String(b.title)))); });
    return () => { live = false; };
  }, [user && user.uid, user && user.committee, user && user.role]);   // eslint-disable-line react-hooks/exhaustive-deps

  if (!list.length && !readErr) return null;
  return (
    <div style={{ border: '1px solid ' + C.line, borderRadius: 8, padding: 14, margin: '16px 0', color: C.text }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: C.accent }}>Violation matrices - confirmed by your General Chairperson <RulingRef user={user} n={310} why="Only entries the General Chairperson confirmed and released are shown here." /></div>
      <div style={{ fontSize: 12, color: C.dim, marginBottom: 8 }}>Each entry is the agreement's own words and how it can be violated, as your General Chairperson confirmed it. The platform does not interpret the agreement.</div>
      {readErr ? <div style={{ fontSize: 12, color: C.bad, marginBottom: 8 }}>&#9888; {readErr}</div> : null}
      {list.map(m => {
        const rules = [];
        for (const e of m.entries) if (!rules.includes(e.rule)) rules.push(e.rule);
        const isOpen = open === m.agreementKey;
        return (
          <div key={m.agreementKey} style={{ borderTop: '1px solid ' + C.line, padding: '8px 0' }}>
            <button onClick={() => { setOpen(isOpen ? '' : m.agreementKey); setOpenRule(''); }} style={{ background: 'none', border: 'none', color: C.text, fontSize: 14, fontWeight: 700, cursor: 'pointer', padding: 0, textAlign: 'left' }}>
              {isOpen ? '▾' : '▸'} {m.title} <span style={{ fontSize: 12, color: C.dim, fontWeight: 400 }}>· {m.entries.length} confirmed entries · {rules.length} rules</span>
            </button>
            <span style={{ marginLeft: 8 }}><SlidesButton build={() => matrixDeck(m)} /></span>
            {isOpen && rules.map(rule => (
              <div key={rule} style={{ marginLeft: 14, marginTop: 6 }}>
                <button onClick={() => setOpenRule(openRule === rule ? '' : rule)} style={{ background: 'none', border: 'none', color: C.accent, fontSize: 13, cursor: 'pointer', padding: 0, textAlign: 'left' }}>
                  {openRule === rule ? '▾' : '▸'} {rule}
                </button>
                {openRule === rule && m.entries.filter(e => e.rule === rule).map(e => (
                  <div key={e.id} style={{ margin: '6px 0 10px 14px' }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: C.accent, textTransform: 'uppercase' }}>{e.label}{e.remedyCap ? <span style={{ color: C.bad }}> · remedy capped by the rule</span> : null}</div>
                    <div style={{ fontSize: 13, margin: '3px 0' }}>{e.how}</div>
                    <div style={{ fontSize: 13, fontStyle: 'italic', color: C.quote, borderLeft: '3px solid ' + C.accent, paddingLeft: 8 }}>“{e.quote}”</div>
                    <div style={{ fontSize: 11, color: C.dim, marginTop: 3 }}>Line {e.sourceLine} · {e.confirmedLine}</div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}
